import { DEFAULT_CURRENCY_TYPE_ISO_CODE } from '@mahawi/eshop-common/dist/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../request-failed/reducer';
import {
  type CreateOrder,
  type CreateOrderResponse,
  type IOrderPaymentRetry,
  type IOrderPaymentRetryResponse,
  type IOrderState,
} from './types';

export const initialState: IOrderState = {
  uuid: '',
  currencyPrice: {
    pricesWithTax: [],
    priceWithoutTax: 0,
    isoCode: DEFAULT_CURRENCY_TYPE_ISO_CODE,
  },
  redirectLink: '',
  isLoading: false,
  orderStatusCode: '',
  products: [],
  packagesCount: 0,
  orderDelivery: {
    fee: 0,
    feeWithoutTax: 0,
    packagesCount: 0,
  },
  discount: {
    discountType: {
      code: '',
    },
    code: '',
    value: 0,
  },
  companyHasPricesWithoutTax: false,
  failure: null,
  isPaymentUnavailable: false,
  isRetryingPayment: false,
  paymentRetryError: false,
};

const orderSlice = createSlice({
  name: 'Order',
  initialState,
  reducers: {
    reset: (): IOrderState => {
      return { ...initialState, isLoading: false };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    create: (state, _action: CreateOrder): void => {
      state.failure = null;
      state.isPaymentUnavailable = false;
    },
    createResponse: (state, action: CreateOrderResponse): void => {
      if (action.payload.failure) {
        state.failure = action.payload.failure;
        return;
      }

      state.failure = null;
      state.uuid = action.payload.uuid;
      state.redirectLink = action.payload.redirectLink;
      state.isPaymentUnavailable =
        action.payload.uuid !== '' && action.payload.redirectLink === '';
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    paymentRetry: (state, _action: IOrderPaymentRetry): void => {
      state.isRetryingPayment = true;
      state.paymentRetryError = false;
    },
    paymentRetryResponse: (state, action: IOrderPaymentRetryResponse): void => {
      state.isRetryingPayment = false;

      if (action.payload.ok && action.payload.redirectLink) {
        state.redirectLink = action.payload.redirectLink;
      } else {
        state.paymentRetryError = true;
      }
    },
  },
  extraReducers: (builder): void => {
    builder.addMatcher(
      requestFailedMatcher('Order'),
      (state: IOrderState): void => {
        resetLoadingOnRequestFailed(state);

        if (state.isRetryingPayment) {
          state.isRetryingPayment = false;
          state.paymentRetryError = true;
        }
      },
    );
  },
});

export const {
  reset,
  create,
  createResponse,
  paymentRetry,
  paymentRetryResponse,
} = orderSlice.actions;

export default orderSlice.reducer;
